import type { Airflow } from '@aviata/platform-types';
import type { PassengersAncillaryState } from '@aviata/ancillary-fns';
import type { MarkedProduct, ProductsState } from '@aviata/products-fns';

import type { SelectedSeatsState } from '@/widgets/seat-map';

import type { productsTypes } from '@/entities/products';

import { useUserStore } from '@/shared/model';
import type { amplitudeEventsConfigTypes } from '@/shared/config';

import {
  googleAnalyticsEventsLib,
  amplitudeEventsLib,
  appsFlyerEventsLib,
  jsBridgeEventsLib,
  bloomreachEventsLib,
  acceleraEventsLib,
} from '..';
import type { extractAnalyticsDataTypes } from '../analytics-data-extraction';

export type ProductsPageOpenParams = {
  products: MarkedProduct[] | null;
  packages: Airflow.Products.Package[] | null;
  isBaggageAvailable: boolean;
  isSeatMapAvailable: boolean;
};

export const productsPageOpen = (
  params?: ProductsPageOpenParams,
  providerClass?: string,
) => {
  // Need to send Bloomreach event only when products page is fully loaded and params were passed
  if (!params) {
    amplitudeEventsLib.productsPageOpen(providerClass);
    jsBridgeEventsLib.additionalProductPageOpened();
  } else {
    bloomreachEventsLib.revenuePageOpen(params);
  }
};

export const bookingError = (error: string) => {
  googleAnalyticsEventsLib.bookingError(error);
  amplitudeEventsLib.bookingError(error);
};

export type BookingSuccessInfo = {
  airlineBonusCards: string;
  orderNumber: string;
  orderId: string;
  packageInfo: productsTypes.ExtendedPackageInfo | null;
  products: ProductsState | null;
  seatMap: SelectedSeatsState | null;
  baggage: Partial<PassengersAncillaryState>;
  medicalInsurance: boolean;
  roaming: boolean;
  source?: amplitudeEventsConfigTypes.DetailsPageOpenSource;
  total?: string;
  providerClass?: string;
  refundable?: boolean;
  connectType?: string;
  isDomestic?: boolean;
  searchQuery?: string;
  /**
   * Current segments data from bookingStore (after fare family change)
   * Used to override segments_data_from/segments_data_to from storage
   * which may contain outdated baggage/hand_luggage info
   */
  segmentsData?: extractAnalyticsDataTypes.SegmentsDataResult;
};

export const bookingSuccess = (info: BookingSuccessInfo) => {
  const isAuthorized = Boolean(useUserStore().user);

  googleAnalyticsEventsLib.bookingSuccess(info);
  amplitudeEventsLib.bookingSuccess(info);
  appsFlyerEventsLib.bookingSuccess(info);
  jsBridgeEventsLib.bookingButtonClicked(info);
  bloomreachEventsLib.successBooking({ ...info, isAuthorized });
  acceleraEventsLib.bookingSucceeded({ ...info, isAuthorized });
};
